'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Node {
  id: string;
  type: 'team' | 'player' | 'goalie' | 'event' | 'coach';
  label: string;
  x: number;
  y: number;
  color: string;
  size: number;
  connections: string[];
}

interface Relation {
  from: string;
  to: string;
  type: string;
  strength: number;
  color: string;
}

interface KnowledgeEntry {
  entity: string;
  type: string;
  relations: { target: string; relationship: string; strength: number }[];
  insights: string[];
}

const KNOWLEDGE_ENTRIES: KnowledgeEntry[] = [
  {
    entity: 'Boston Bruins',
    type: 'team',
    relations: [
      { target: 'Jeremy Swayman', relationship: 'Starting Goalie', strength: 95 },
      { target: 'Toronto Maple Leafs', relationship: 'Historical Rivalry', strength: 88 },
      { target: 'Jim Montgomery', relationship: 'Head Coach', strength: 100 },
      { target: 'David Pastrnak', relationship: 'Star Forward', strength: 97 },
    ],
    insights: ['Dominant home record (34-8)', 'Strong PP% (24.1%)', 'B2B performance drops 12%', 'Swayman: .924 SV% last 10'],
  },
  {
    entity: 'Colorado Avalanche',
    type: 'team',
    relations: [
      { target: 'Alexandar Georgiev', relationship: 'Starting Goalie', strength: 88 },
      { target: 'Nathan MacKinnon', relationship: 'MVP Candidate', strength: 99 },
      { target: 'Jared Bednar', relationship: 'Head Coach', strength: 100 },
      { target: 'Vegas Golden Knights', relationship: 'Division Rival', strength: 82 },
    ],
    insights: ['Elite xG generation (3.41/60)', 'MacKinnon: 1.42 pts/game', 'Altitude advantage at home', 'Best PP in Western Conference'],
  },
  {
    entity: 'Jeremy Swayman',
    type: 'goalie',
    relations: [
      { target: 'Boston Bruins', relationship: 'Team', strength: 95 },
      { target: 'Auston Matthews', relationship: 'Historical Matchup', strength: 71 },
      { target: 'Nathan MacKinnon', relationship: 'Historical Matchup', strength: 68 },
    ],
    insights: ['.924 SV% season', 'Best vs high-danger shots', 'Struggles in B2B', '3+ days rest: .931 SV%'],
  },
];

const GRAPH_NODES: Node[] = [
  { id: 'bos', type: 'team', label: 'BOS', x: 200, y: 150, color: '#f59e0b', size: 28, connections: ['swayman', 'pasta', 'tor', 'montgomery'] },
  { id: 'tor', type: 'team', label: 'TOR', x: 380, y: 120, color: '#3b82f6', size: 24, connections: ['matthews', 'bos'] },
  { id: 'col', type: 'team', label: 'COL', x: 120, y: 300, color: '#a855f7', size: 26, connections: ['mack', 'georgiev', 'vgk'] },
  { id: 'vgk', type: 'team', label: 'VGK', x: 300, y: 320, color: '#10b981', size: 22, connections: ['col'] },
  { id: 'swayman', type: 'goalie', label: 'Swayman', x: 150, y: 80, color: '#06b6d4', size: 18, connections: ['bos', 'matthews'] },
  { id: 'pasta', type: 'player', label: 'Pastrnak', x: 280, y: 200, color: '#ef4444', size: 16, connections: ['bos'] },
  { id: 'matthews', type: 'player', label: 'Matthews', x: 450, y: 200, color: '#3b82f6', size: 20, connections: ['tor', 'swayman'] },
  { id: 'mack', type: 'player', label: 'MacKinnon', x: 80, y: 220, color: '#a855f7', size: 22, connections: ['col'] },
  { id: 'georgiev', type: 'goalie', label: 'Georgiev', x: 200, y: 380, color: '#06b6d4', size: 16, connections: ['col'] },
  { id: 'montgomery', type: 'coach', label: 'Montgomery', x: 320, y: 80, color: '#64748b', size: 14, connections: ['bos'] },
];

const RELATIONS: Relation[] = [
  { from: 'bos', to: 'swayman', type: 'Starting Goalie', strength: 95, color: '#06b6d4' },
  { from: 'bos', to: 'pasta', type: 'Star Forward', strength: 97, color: '#ef4444' },
  { from: 'bos', to: 'tor', type: 'Rivalry', strength: 88, color: '#f59e0b' },
  { from: 'bos', to: 'montgomery', type: 'Head Coach', strength: 100, color: '#64748b' },
  { from: 'tor', to: 'matthews', type: 'Star Forward', strength: 99, color: '#3b82f6' },
  { from: 'swayman', to: 'matthews', type: 'Matchup History', strength: 71, color: '#a855f7' },
  { from: 'col', to: 'mack', type: 'MVP Candidate', strength: 99, color: '#a855f7' },
  { from: 'col', to: 'georgiev', type: 'Starting Goalie', strength: 88, color: '#06b6d4' },
  { from: 'col', to: 'vgk', type: 'Division Rival', strength: 82, color: '#10b981' },
];

const TYPE_COLORS: Record<string, string> = {
  team: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
  player: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  goalie: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
  coach: 'text-gray-400 bg-gray-400/10 border-gray-400/30',
  event: 'text-red-400 bg-red-400/10 border-red-400/30',
};

export default function KnowledgeGraphClient() {
  const [selectedNode, setSelectedNode] = useState<Node | null>(GRAPH_NODES[0]);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry>(KNOWLEDGE_ENTRIES[0]);
  const [hoveredRelation, setHoveredRelation] = useState<string | null>(null);

  const getNodeById = (id: string) => GRAPH_NODES.find(n => n.id === id);

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="ShareIcon" size={20} className="text-indigo-400" />
            <h1 className="text-xl font-bold text-foreground">Hockey Knowledge Graph</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-indigo-400/10 text-indigo-400 border border-indigo-400/20 rounded">PHASE QUANT · ÉTAPE 7</span>
          </div>
          <p className="text-sm text-muted-foreground">Moteur relationnel contextuel — équipes, joueurs, gardiens, événements</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">NŒUDS</div>
            <div className="text-lg font-bold text-indigo-400 font-mono">{GRAPH_NODES.length}</div>
          </div>
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">RELATIONS</div>
            <div className="text-lg font-bold text-indigo-400 font-mono">{RELATIONS.length}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* SVG Graph */}
        <div className="col-span-2 bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Graphe Relationnel Interactif</h3>
          <div className="relative bg-background rounded-lg border border-border overflow-hidden" style={{ height: 420 }}>
            <svg width="100%" height="100%" viewBox="0 0 520 420">
              {/* Relations */}
              {RELATIONS.map((rel, i) => {
                const from = getNodeById(rel.from);
                const to = getNodeById(rel.to);
                if (!from || !to) return null;
                const isHovered = hoveredRelation === `${rel.from}-${rel.to}`;
                return (
                  <g key={i}>
                    <line
                      x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                      stroke={rel.color}
                      strokeWidth={isHovered ? 3 : Math.max(1, rel.strength / 40)}
                      strokeOpacity={isHovered ? 0.9 : 0.4}
                      strokeDasharray={rel.type === 'Rivalry' ? '6 3' : undefined}
                      onMouseEnter={() => setHoveredRelation(`${rel.from}-${rel.to}`)}
                      onMouseLeave={() => setHoveredRelation(null)}
                      style={{ cursor: 'pointer' }}
                    />
                    {isHovered && (
                      <text x={(from.x + to.x) / 2} y={(from.y + to.y) / 2 - 6} fill={rel.color} fontSize={9} textAnchor="middle">
                        {rel.type}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* Nodes */}
              {GRAPH_NODES.map(node => {
                const isSelected = selectedNode?.id === node.id;
                return (
                  <g key={node.id} onClick={() => setSelectedNode(node)} style={{ cursor: 'pointer' }}>
                    <circle cx={node.x} cy={node.y} r={node.size + (isSelected ? 4 : 0)}
                      fill={node.color + '20'} stroke={node.color} strokeWidth={isSelected ? 3 : 1.5} />
                    <text x={node.x} y={node.y + 4} fill={node.color} fontSize={node.type === 'team' ? 11 : 9}
                      textAnchor="middle" fontWeight={node.type === 'team' ? 'bold' : 'normal'}>
                      {node.label}
                    </text>
                    <text x={node.x} y={node.y + node.size + 12} fill="#64748b" fontSize={8} textAnchor="middle">
                      {node.type}
                    </text>
                  </g>
                );
              })}
            </svg>
            {/* Legend */}
            <div className="absolute bottom-3 left-3 flex gap-3">
              {Object.entries({ team: '#f59e0b', player: '#3b82f6', goalie: '#06b6d4', coach: '#64748b' }).map(([type, color]) => (
                <div key={type} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-2xs text-muted-foreground capitalize">{type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Node Details */}
        <div className="space-y-3">
          {selectedNode && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedNode.color }} />
                <h3 className="text-sm font-semibold text-foreground">{selectedNode.label}</h3>
                <span className={`text-2xs px-1.5 py-0.5 rounded border font-mono ${TYPE_COLORS[selectedNode.type]}`}>{selectedNode.type.toUpperCase()}</span>
              </div>
              <div className="space-y-1.5">
                <div className="text-2xs text-muted-foreground font-mono uppercase mb-2">Connexions</div>
                {selectedNode.connections.map(connId => {
                  const conn = getNodeById(connId);
                  const rel = RELATIONS.find(r => (r.from === selectedNode.id && r.to === connId) || (r.to === selectedNode.id && r.from === connId));
                  if (!conn) return null;
                  return (
                    <div key={connId} className="flex items-center justify-between p-2 bg-background rounded border border-border">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: conn.color }} />
                        <span className="text-xs text-foreground">{conn.label}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xs text-muted-foreground">{rel?.type}</div>
                        <div className="text-2xs font-mono text-green-400">{rel?.strength}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Knowledge entries */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Insights Contextuels</h3>
            <div className="flex gap-1 mb-3 flex-wrap">
              {KNOWLEDGE_ENTRIES.map(e => (
                <button key={e.entity} onClick={() => setSelectedEntry(e)}
                  className={`px-2 py-1 text-2xs rounded border transition-colors ${selectedEntry.entity === e.entity ? 'bg-indigo-400/20 border-indigo-400 text-indigo-400' : 'border-border text-muted-foreground'}`}>
                  {e.entity.split(' ').pop()}
                </button>
              ))}
            </div>
            <div className="space-y-1.5">
              {selectedEntry.insights.map((insight, i) => (
                <div key={i} className="flex items-start gap-2 p-2 bg-background rounded border border-border">
                  <Icon name="LightBulbIcon" size={12} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-foreground">{insight}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
